// libs/shared/ui-components/src/AuditLog/useAuditLog.ts
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hasPermission } from '@ameroservices-platform/shared/utility/index';
import {
  requestAuditLog,
  requestAuditLogLimited,
  requestUser,
  selectAuditLogRecords,
  selectAuditLogUsers,
} from './auditLogSlice';
import type { AuditLogRecord } from './AuditLogPanel.types';

const SYSTEM_ACTOR_IDS = ['SYSTEM', 'notProvided', 'FLEXPOS', 'CUSTOMER'];

interface UseAuditLogResult {
  records: AuditLogRecord[];
  users: Record<string, { id: string; name: string }>;
}

function useAuditLog(
  type: string,
  uid: string,
  orderBy?: string,
  limit?: number,
  enabled = true
): UseAuditLogResult {
  const dispatch = useDispatch();
  const records = useSelector(selectAuditLogRecords);
  const users = useSelector(selectAuditLogUsers);
  const userRole = useSelector(({ auth }: any) => auth.user.role);
  const isAmeroAdmin = useMemo(
    () => hasPermission(['ameroAdmin'], userRole),
    [userRole]
  );

  useEffect(() => {
    if (!enabled) return;
    let unsubscribe: (() => void) | undefined;
    if (orderBy || limit) {
      unsubscribe = dispatch(
        requestAuditLogLimited(type, uid, orderBy, limit)
      ) as unknown as () => void;
    } else {
      unsubscribe = dispatch(
        requestAuditLog(type, uid)
      ) as unknown as () => void;
    }
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [dispatch, type, uid, orderBy, limit, enabled]);

  useEffect(() => {
    const uniqueIds = [...new Set(records.map((r) => r.changedBy))];
    uniqueIds.forEach((id) => {
      if (!SYSTEM_ACTOR_IDS.includes(id) && !users[id]) {
        dispatch(requestUser(id, isAmeroAdmin));
      }
    });
  }, [dispatch, records, users, isAmeroAdmin]);

  return { records, users };
}

export default useAuditLog;
